import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Users, Building2, Stethoscope, Heart, Shield, Clock, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const [stats, setStats] = useState({ providers: 0, locations: 0, states: 0 });
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/services`),
      ]);
      setStats(statsRes.data);
      setServices(servicesRes.data.slice(0, 6));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const serviceIcons = {
    Stethoscope: Stethoscope,
    Heart: Heart,
    Users: Users,
    Shield: Shield,
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative bg-[#F1FAEE] overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-1.5 bg-[#00B4D8]/10 text-[#00B4D8] text-sm font-medium rounded-full mb-6">
                National Medical Network
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#14213D] leading-tight mb-6">
                Quality Healthcare
                <span className="text-[#00B4D8] block">Across America</span>
              </h1>
              <p className="text-gray-600 text-lg leading-relaxed mb-8 max-w-lg">
                Medical Access Inc connects patients with trusted healthcare providers nationwide. 
                Find quality care in your community with our extensive network.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/find-provider">
                  <Button
                    className="bg-[#14213D] text-white rounded-lg px-6 py-6 text-base hover:bg-[#1a2d4d] transition-colors"
                    data-testid="hero-find-provider"
                  >
                    Find a Provider
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/locations">
                  <Button
                    variant="outline"
                    className="border-[#14213D] text-[#14213D] rounded-lg px-6 py-6 text-base hover:bg-[#14213D]/5"
                    data-testid="hero-view-locations"
                  >
                    View Locations
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right - US Map Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative bg-white rounded-2xl shadow-xl p-8 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800"
                  alt="Healthcare professionals"
                  className="w-full h-80 object-cover rounded-xl"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-xl p-4 shadow-lg">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="font-heading font-bold text-2xl text-[#14213D]">{stats.providers}+</p>
                      <p className="text-xs text-gray-500">Providers</p>
                    </div>
                    <div>
                      <p className="font-heading font-bold text-2xl text-[#00B4D8]">{stats.locations}+</p>
                      <p className="text-xs text-gray-500">Locations</p>
                    </div>
                    <div>
                      <p className="font-heading font-bold text-2xl text-[#14213D]">{stats.states}+</p>
                      <p className="text-xs text-gray-500">States</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#14213D] py-12" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "500K+", label: "Patients Served", icon: Users },
              { value: "25+", label: "Years Experience", icon: Clock },
              { value: `${stats.locations}+`, label: "Locations", icon: Building2 },
              { value: "98%", label: "Patient Satisfaction", icon: Heart },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <stat.icon className="w-8 h-8 text-[#00B4D8] mx-auto mb-3" />
                <p className="font-heading font-bold text-3xl text-white">{stat.value}</p>
                <p className="text-gray-400 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-[#00B4D8] text-sm font-medium uppercase tracking-wider">Our Services</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#14213D] mt-2">
              Comprehensive Healthcare Solutions
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#F1FAEE] rounded-xl p-6 card-hover cursor-pointer group"
                data-testid={`service-card-${index}`}
              >
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#00B4D8] transition-colors">
                  <Stethoscope className="w-6 h-6 text-[#14213D] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#14213D] mb-2">
                  {service.name}
                </h3>
                <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                <Link to="/services" className="inline-flex items-center text-[#00B4D8] text-sm font-medium">
                  Learn More <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/services">
              <Button
                variant="outline"
                className="border-[#14213D] text-[#14213D] rounded-lg px-6 py-5"
                data-testid="view-all-services"
              >
                View All Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#00B4D8] cta-section" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Find Your Healthcare Provider?
              </h2>
              <p className="text-white/90 text-lg mb-8">
                Search our network of qualified physicians and specialists. 
                Book appointments online and access quality care near you.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/find-provider">
                  <Button
                    className="bg-white text-[#00B4D8] rounded-lg px-6 py-6 hover:bg-gray-100 transition-colors"
                    data-testid="cta-search-providers"
                  >
                    Search Providers
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    variant="outline"
                    className="border-white text-white rounded-lg px-6 py-6 hover:bg-white/10"
                    data-testid="cta-contact"
                  >
                    Contact Us
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 rounded-2xl p-8 backdrop-blur"
            >
              <h3 className="font-heading font-semibold text-xl text-white mb-6">
                Why Choose Medical Access?
              </h3>
              <ul className="space-y-4">
                {[
                  "Nationwide network of verified providers",
                  "Easy online appointment booking",
                  "Accepted by major insurance plans",
                  "Telehealth options available",
                  "24/7 patient support",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-white/90">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                      <ChevronRight className="w-4 h-4" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Locations Preview */}
      <section className="py-20 bg-[#F1FAEE]" data-testid="locations-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-[#00B4D8] text-sm font-medium uppercase tracking-wider">Our Network</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#14213D] mt-2">
              Locations Across America
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              With facilities in major cities nationwide, quality healthcare is never far away.
            </p>
          </motion.div>

          <div className="relative bg-white rounded-2xl shadow-lg p-8 overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1569974507005-6dc61f97fb5c?w=1200"
              alt="US Map"
              className="w-full h-64 md:h-96 object-contain opacity-20"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
                {["New York", "Los Angeles", "Chicago", "Houston", "Miami", "Phoenix"].map((city, i) => (
                  <motion.div
                    key={city}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-2 bg-white/90 backdrop-blur px-4 py-2 rounded-lg shadow"
                  >
                    <MapPin className="w-4 h-4 text-[#00B4D8]" />
                    <span className="text-sm font-medium text-[#14213D]">{city}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link to="/locations">
              <Button
                className="bg-[#14213D] text-white rounded-lg px-6 py-5"
                data-testid="view-all-locations"
              >
                View All Locations
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Patient Portal CTA */}
      <section className="py-20 bg-white" data-testid="portal-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#14213D] rounded-2xl p-8 md:p-12 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold text-white mb-4">
                Access Your Patient Portal
              </h2>
              <p className="text-gray-300 mb-6">
                View your medical records, schedule appointments, and communicate with your 
                healthcare team - all in one secure place.
              </p>
              <Button
                className="bg-[#00B4D8] text-white rounded-lg px-6 py-5 hover:bg-[#0096c7]"
                data-testid="patient-portal-btn"
              >
                Log In to Portal
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: "📋", title: "Medical Records", desc: "Access your health history" },
                { icon: "📅", title: "Appointments", desc: "Schedule & manage visits" },
                { icon: "💬", title: "Messaging", desc: "Contact your care team" },
                { icon: "💊", title: "Prescriptions", desc: "Refill medications" },
              ].map((item, i) => (
                <div key={i} className="bg-white/10 rounded-xl p-4">
                  <span className="text-2xl mb-2 block">{item.icon}</span>
                  <h4 className="font-medium text-white text-sm">{item.title}</h4>
                  <p className="text-gray-400 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
